import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faTimes, faCheckCircle, faExclamationTriangle,
  faFileExport, faLink, faSearch, faFilter, faCheck, faChevronDown, faChevronUp, faTag
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import config from '../config/config';
import './ContentAssignPage.css';

// =============================================================
// Content Picker Modal
// =============================================================
const ContentPickerModal = ({ isOpen, onClose, onConfirm, alreadySelected, getAccessToken }) => {
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterOptions, setFilterOptions] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [assetTypeFilter, setAssetTypeFilter] = useState('ALL');
  const [bundleTypeFilter, setBundleTypeFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [visibilityFilter, setVisibilityFilter] = useState('ALL');
  const [tagFilters, setTagFilters] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(alreadySelected.map(s => `${s.type}-${s.id}`));
    setSearchQuery('');
    setTypeFilter('ALL');
    setAssetTypeFilter('ALL');
    setBundleTypeFilter('ALL');
    setCategoryFilter('ALL');
    setVisibilityFilter('ALL');
    setTagFilters([]);
    setShowAdvancedFilters(false);
    loadAllContent();
    loadFilterOptions();
  }, [isOpen]);

  const loadAllContent = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/assign/list-all`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok && result.status === 'success') {
        setAllItems(result.data || []);
        setFilteredItems(result.data || []);
      }
    } catch (err) {
      console.error('Error loading content:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/assign/filter-options`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok && result.status === 'success') {
        setFilterOptions(result.data);
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  useEffect(() => {
    let items = allItems;

    if (typeFilter !== 'ALL') {
      items = items.filter(i => i.type === typeFilter);
    }

    if (assetTypeFilter !== 'ALL') {
      items = items.filter(i => i.type === 'ASSET' && i.assetType === assetTypeFilter);
    }

    if (bundleTypeFilter !== 'ALL') {
      items = items.filter(i => i.type === 'BUNDLE' && i.bundleType === bundleTypeFilter);
    }

    if (categoryFilter !== 'ALL') {
      items = items.filter(i => i.categoryName === categoryFilter);
    }

    if (visibilityFilter !== 'ALL') {
      items = items.filter(i => i.type === 'ASSET' && i.visibility === visibilityFilter);
    }

    if (tagFilters.length > 0) {
      items = items.filter(i =>
        i.tags && i.tags.length > 0 && tagFilters.some(tf => i.tags.includes(tf))
      );
    }

    if (searchQuery.trim().length > 0) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        i.title.toLowerCase().includes(q) ||
        (i.description && i.description.toLowerCase().includes(q))
      );
    }
    setFilteredItems(items);
  }, [searchQuery, typeFilter, assetTypeFilter, bundleTypeFilter, categoryFilter, visibilityFilter, tagFilters, allItems]);

  const toggleItem = (item) => {
    const key = `${item.type}-${item.id}`;
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const isSelected = (item) => selected.includes(`${item.type}-${item.id}`);

  const handleConfirm = () => {
    const selectedItems = allItems.filter(item => selected.includes(`${item.type}-${item.id}`));
    onConfirm(selectedItems);
    onClose();
  };

  const toggleTagFilter = (tagName) => {
    setTagFilters(prev =>
      prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName]
    );
  };

  const clearAllFilters = () => {
    setTypeFilter('ALL');
    setAssetTypeFilter('ALL');
    setBundleTypeFilter('ALL');
    setCategoryFilter('ALL');
    setVisibilityFilter('ALL');
    setTagFilters([]);
    setSearchQuery('');
  };

  const activeFilterCount = [
    typeFilter !== 'ALL',
    assetTypeFilter !== 'ALL',
    bundleTypeFilter !== 'ALL',
    categoryFilter !== 'ALL',
    visibilityFilter !== 'ALL',
    tagFilters.length > 0
  ].filter(Boolean).length;

  const selectedCount = selected.length;
  const assetCount = selected.filter(k => k.startsWith('ASSET-')).length;
  const bundleCount = selected.filter(k => k.startsWith('BUNDLE-')).length;

  if (!isOpen) return null;

  return (
    <div className="picker-modal-overlay" onClick={onClose}>
      <div className="picker-modal" onClick={e => e.stopPropagation()}>
        <div className="picker-modal-header">
          <h3>Select Content & Bundles</h3>
          <button className="picker-modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        <div className="picker-modal-toolbar">
          <div className="picker-search-box">
            <FontAwesomeIcon icon={faSearch} className="picker-search-icon" />
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="picker-filters-row">
            <div className="picker-filters">
              <FontAwesomeIcon icon={faFilter} className="picker-filter-icon" />
              <button className={`picker-filter-btn ${typeFilter === 'ALL' ? 'active' : ''}`} onClick={() => setTypeFilter('ALL')}>All</button>
              <button className={`picker-filter-btn ${typeFilter === 'ASSET' ? 'active' : ''}`} onClick={() => setTypeFilter('ASSET')}>Assets</button>
              <button className={`picker-filter-btn ${typeFilter === 'BUNDLE' ? 'active' : ''}`} onClick={() => setTypeFilter('BUNDLE')}>Bundles</button>
            </div>
            <button
              className={`picker-advanced-toggle ${showAdvancedFilters ? 'active' : ''}`}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              More Filters {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
              <FontAwesomeIcon icon={showAdvancedFilters ? faChevronUp : faChevronDown} />
            </button>
          </div>

          {showAdvancedFilters && filterOptions && (
            <div className="picker-advanced-filters">
              <div className="advanced-filter-grid">
                {(typeFilter === 'ALL' || typeFilter === 'ASSET') && filterOptions.assetTypes && filterOptions.assetTypes.length > 0 && (
                  <div className="advanced-filter-group">
                    <label>Asset Type</label>
                    <select value={assetTypeFilter} onChange={e => setAssetTypeFilter(e.target.value)}>
                      <option value="ALL">All Types</option>
                      {filterOptions.assetTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(typeFilter === 'ALL' || typeFilter === 'BUNDLE') && filterOptions.bundleTypes && filterOptions.bundleTypes.length > 0 && (
                  <div className="advanced-filter-group">
                    <label>Bundle Type</label>
                    <select value={bundleTypeFilter} onChange={e => setBundleTypeFilter(e.target.value)}>
                      <option value="ALL">All Types</option>
                      {filterOptions.bundleTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                )}

                {filterOptions.categories && filterOptions.categories.length > 0 && (
                  <div className="advanced-filter-group">
                    <label>Category</label>
                    <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                      <option value="ALL">All Categories</option>
                      {filterOptions.categories.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(typeFilter === 'ALL' || typeFilter === 'ASSET') && filterOptions.visibilities && filterOptions.visibilities.length > 0 && (
                  <div className="advanced-filter-group">
                    <label>Visibility</label>
                    <select value={visibilityFilter} onChange={e => setVisibilityFilter(e.target.value)}>
                      <option value="ALL">All</option>
                      {filterOptions.visibilities.map(v => (
                        <option key={v} value={v}>{v.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {filterOptions.tags && filterOptions.tags.length > 0 && (
                <div className="advanced-filter-tags">
                  <label><FontAwesomeIcon icon={faTag} /> Tags</label>
                  <div className="tag-chips-container">
                    {filterOptions.tags.map(tag => (
                      <button
                        key={tag.name}
                        className={`tag-filter-chip ${tagFilters.includes(tag.name) ? 'active' : ''}`}
                        onClick={() => toggleTagFilter(tag.name)}
                        style={tag.color ? {
                          '--tag-color': tag.color,
                          '--tag-bg': tag.color + '20'
                        } : {}}
                      >
                        {tag.color && <span className="tag-dot" style={{ backgroundColor: tag.color }}></span>}
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {activeFilterCount > 0 && (
                <button className="clear-all-filters-btn" onClick={clearAllFilters}>
                  <FontAwesomeIcon icon={faTimes} /> Clear All Filters
                </button>
              )}
            </div>
          )}
        </div>

        <div className="picker-modal-body">
          {loading ? (
            <div className="picker-loading">
              <div className="picker-loading-spinner"></div>
              <p>Loading content...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="picker-empty">No content found matching your criteria.</div>
          ) : (
            <div className="picker-list">
              {filteredItems.map(item => (
                <div
                  key={`${item.type}-${item.id}`}
                  className={`picker-item ${isSelected(item) ? 'picker-item-selected' : ''}`}
                  onClick={() => toggleItem(item)}
                >
                  <div className="picker-item-checkbox">
                    {isSelected(item) && <FontAwesomeIcon icon={faCheck} />}
                  </div>
                  <div className="picker-item-info">
                    <div className="picker-item-title">{item.title}</div>
                    {item.description && <div className="picker-item-desc">{item.description}</div>}
                    <div className="picker-item-meta">
                      {item.assetType && <span className="picker-meta-badge meta-asset-type">{item.assetType}</span>}
                      {item.bundleType && <span className="picker-meta-badge meta-bundle-type">{item.bundleType}</span>}
                      {item.categoryName && <span className="picker-meta-badge meta-category">{item.categoryName}</span>}
                      {item.visibility && <span className="picker-meta-badge meta-visibility">{item.visibility.replace('_', ' ')}</span>}
                      {item.tags && item.tags.map(tag => (
                        <span key={tag} className="picker-meta-badge meta-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <span className={`picker-item-type type-${item.type.toLowerCase()}`}>{item.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="picker-modal-footer">
          <div className="picker-selection-summary">
            {selectedCount > 0
              ? `${selectedCount} selected (${assetCount} asset${assetCount !== 1 ? 's' : ''}, ${bundleCount} bundle${bundleCount !== 1 ? 's' : ''})`
              : 'No items selected'}
          </div>
          <div className="picker-modal-actions">
            <button className="picker-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="picker-btn-confirm" onClick={handleConfirm} disabled={selectedCount === 0}>
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// Main Page
// =============================================================
const ContentAssignPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  const [activeTab, setActiveTab] = useState('bulk');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // --- Content Picker Modal State ---
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);

  // --- Bulk Assign State ---
  const [file, setFile] = useState(null);
  const [bulkSelectedContent, setBulkSelectedContent] = useState([]);
  const [bulkExpiryDate, setBulkExpiryDate] = useState(null);

  // --- Manual Assign State ---
  const [entries, setEntries] = useState([createEmptyEntry()]);

  function createEmptyEntry() {
    return {
      id: Date.now() + Math.random(),
      iboNumber: '',
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      showDropdown: false,
      userData: null,
      validated: false,
      validatedViaAmway: false,
      validationError: null,
      selectedContent: [],
      expiryDate: null,
    };
  }

  // =====================
  // Content Picker Handlers
  // =====================
  const openPickerForBulk = () => {
    setPickerTarget({ type: 'bulk' });
    setPickerOpen(true);
  };

  const openPickerForEntry = (entryId) => {
    setPickerTarget({ type: 'entry', entryId });
    setPickerOpen(true);
  };

  const getPickerAlreadySelected = () => {
    if (!pickerTarget) return [];
    if (pickerTarget.type === 'bulk') return bulkSelectedContent;
    const entry = entries.find(e => e.id === pickerTarget.entryId);
    return entry ? entry.selectedContent : [];
  };

  const handlePickerConfirm = (selectedItems) => {
    if (!pickerTarget) return;
    if (pickerTarget.type === 'bulk') {
      setBulkSelectedContent(selectedItems);
    } else {
      setEntries(prev => prev.map(entry =>
        entry.id === pickerTarget.entryId ? { ...entry, selectedContent: selectedItems } : entry
      ));
    }
  };

  // =====================
  // Bulk Tab Handlers
  // =====================
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type !== 'text/csv') {
      setError('Please upload a CSV file only');
      setFile(null);
      e.target.value = null;
    } else {
      setFile(selectedFile);
      setError(null);
    }
  };

  const removeBulkContent = (item) => {
    setBulkSelectedContent(prev => prev.filter(c => !(c.id === item.id && c.type === item.type)));
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/assign/template`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'content-assign-template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      setError('Failed to download template');
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a CSV file to upload'); return; }
    if (bulkSelectedContent.length === 0) { setError('Please select at least one content item or bundle'); return; }

    try {
      setLoading(true);
      setError(null);
      setSuccessData(null);

      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const formData = new FormData();
      formData.append('file', file);

      const assetIds = bulkSelectedContent.filter(c => c.type === 'ASSET').map(c => c.id);
      const bundleIds = bulkSelectedContent.filter(c => c.type === 'BUNDLE').map(c => c.id);

      assetIds.forEach(id => formData.append('assetIds', id));
      bundleIds.forEach(id => formData.append('bundleIds', id));

      if (bulkExpiryDate) {
        formData.append('expiresAt', bulkExpiryDate.toISOString().slice(0, 19));
      }

      const response = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/assign/bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      if (result.status === 'success') {
        setSuccessData(result.data);
        setFile(null);
        const fileInput = document.getElementById('ca-file-upload');
        if (fileInput) fileInput.value = '';
      } else {
        throw new Error(result.message || 'Failed to assign content');
      }
    } catch (err) {
      console.error('Error assigning content:', err);
      setError(err.message || 'An error occurred during assignment');
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // Manual Tab Handlers
  // =====================
  const updateEntry = (entryId, updates) => {
    setEntries(prev => prev.map(entry =>
      entry.id === entryId ? { ...entry, ...updates } : entry
    ));
  };

  const addEntry = () => {
    setEntries(prev => [...prev, createEmptyEntry()]);
  };

  const removeEntry = (entryId) => {
    setEntries(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(entry => entry.id !== entryId);
    });
  };

  // IBO user search
  const searchUsers = useCallback(async (query, entryId) => {
    if (!query || query.length < 2) {
      updateEntry(entryId, { searchResults: [], showDropdown: false, searchLoading: false });
      return;
    }
    updateEntry(entryId, { searchLoading: true });
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/tickets/users/search?search=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        }
      );
      const result = await response.json();
      if (response.ok && result.status === 'success') {
        updateEntry(entryId, { searchResults: result.data || [], showDropdown: true, searchLoading: false });
      } else {
        updateEntry(entryId, { searchResults: [], showDropdown: false, searchLoading: false });
      }
    } catch (err) {
      console.error('Error searching users:', err);
      updateEntry(entryId, { searchResults: [], showDropdown: false, searchLoading: false });
    }
  }, [getAccessToken]);

  const handleIboSearchInput = (entryId, value) => {
    updateEntry(entryId, {
      searchQuery: value,
      iboNumber: '',
      userData: null,
      validated: false,
      validatedViaAmway: false,
      validationError: null
    });
  };

  const handleIboSearchClick = (entryId, query) => {
    searchUsers(query, entryId);
  };

  const handleIboKeyDown = (e, entryId, query) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchUsers(query, entryId);
    }
  };

  const selectUser = async (entryId, user) => {
    updateEntry(entryId, {
      iboNumber: user.iboNumber,
      searchQuery: `${user.iboNumber} - ${user.iboName || ''}`,
      showDropdown: false,
      searchResults: [],
      validated: true,
      validatedViaAmway: false,
      validationError: null
    });
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/get?iboNumber=${user.iboNumber}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (response.ok && result.status === 'success' && result.data) {
        updateEntry(entryId, { userData: result.data });
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const validateIboViaAmway = async (entryId, iboNumber) => {
    if (!iboNumber || iboNumber.trim().length === 0) return;
    updateEntry(entryId, { searchLoading: true, validationError: null });
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/validate-ticket-eligibility?iboNumber=${encodeURIComponent(iboNumber.trim())}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (response.ok && result.data) {
        if (result.data.eligible) {
          updateEntry(entryId, { iboNumber: iboNumber.trim(), validated: true, validatedViaAmway: true, validationError: null, searchLoading: false });
        } else {
          updateEntry(entryId, { validated: false, validatedViaAmway: false, validationError: result.data.message || 'IBO is not eligible', searchLoading: false });
        }
      } else {
        updateEntry(entryId, { validated: false, validationError: 'Could not validate IBO number', searchLoading: false });
      }
    } catch (err) {
      console.error('Error validating IBO:', err);
      updateEntry(entryId, { validated: false, validationError: 'Validation failed. Please try again.', searchLoading: false });
    }
  };

  const handleIboSearchBlur = (entryId, entry) => {
    setTimeout(() => {
      updateEntry(entryId, { showDropdown: false });
      if (!entry.validated && entry.searchQuery && !entry.iboNumber) {
        const possibleIbo = entry.searchQuery.trim();
        if (/^\d+$/.test(possibleIbo)) {
          updateEntry(entryId, { iboNumber: possibleIbo });
          validateIboViaAmway(entryId, possibleIbo);
        }
      }
    }, 200);
  };

  const removeEntryContent = (entryId, item) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id !== entryId) return entry;
      return { ...entry, selectedContent: entry.selectedContent.filter(c => !(c.id === item.id && c.type === item.type)) };
    }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const validEntries = entries.filter(entry => entry.iboNumber && entry.validated && entry.selectedContent.length > 0);
    if (validEntries.length === 0) {
      setError('Please add at least one valid entry with a validated IBO and selected content');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessData(null);

      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const payload = {
        entries: validEntries.map(entry => ({
          iboNumber: entry.iboNumber,
          assetIds: entry.selectedContent.filter(c => c.type === 'ASSET').map(c => c.id),
          bundleIds: entry.selectedContent.filter(c => c.type === 'BUNDLE').map(c => Number(c.id)),
          expiresAt: entry.expiryDate ? entry.expiryDate.toISOString().slice(0, 19) : null
        }))
      };

      const response = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/assign/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (result.status === 'success') {
        setSuccessData(result.data);
        setEntries([createEmptyEntry()]);
      } else {
        throw new Error(result.message || 'Failed to assign content');
      }
    } catch (err) {
      console.error('Error assigning content:', err);
      setError(err.message || 'An error occurred during assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate('/');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    setSuccessData(null);
  };

  // =====================
  // Shared Sub-Components
  // =====================
  const ContentChips = ({ items, onRemove }) => (
    <div className="selected-content-chips">
      {items.map((item) => (
        <span key={`${item.type}-${item.id}`} className={`content-chip chip-${item.type.toLowerCase()}`}>
          <span className="chip-type">{item.type}</span>
          {item.title}
          <button type="button" className="chip-remove" onClick={() => onRemove(item)}>&times;</button>
        </span>
      ))}
    </div>
  );

  // =====================
  // Render
  // =====================
  return (
    <div className="content-assign-container">
      {/* Loading Overlay */}
      {loading && (
        <div className="ca-loading-overlay">
          <div className="ca-loading-box">
            <div className="ca-loading-spinner"></div>
            <p>Processing assignment...</p>
          </div>
        </div>
      )}

      {/* Content Picker Modal */}
      <ContentPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        alreadySelected={getPickerAlreadySelected()}
        getAccessToken={getAccessToken}
      />

      <div className="main-content">
        <button className="back-button" onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        <div className="content-assign-header">
          <h2>Assign Content</h2>
          <p>Assign content and bundles to IBOs via CSV or manually</p>
        </div>

        <div className="tab-bar">
          <button className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`} onClick={() => handleTabChange('bulk')}>
            Bulk Assign
          </button>
          <button className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`} onClick={() => handleTabChange('manual')}>
            Manual Assign
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {successData && (
          <div className="success-message">
            <p>Content assigned successfully!</p>
            <div className="assign-stats">
              <p>Total IBOs: {successData.totalIbos}</p>
              <p>Total Content Items: {successData.totalContentItems}</p>
              <p>Assigned: {successData.assignedCount}</p>
              <p>Skipped (already had access): {successData.skippedCount}</p>
              {successData.failedCount > 0 && <p>Failed: {successData.failedCount}</p>}
            </div>
            {successData.entryResults && successData.entryResults.length > 0 && (
              <div className="entry-results-table">
                <table>
                  <thead>
                    <tr>
                      <th>IBO Number</th>
                      <th>Content</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {successData.entryResults.map((result, idx) => (
                      <tr key={idx} className={`result-row result-${result.status.toLowerCase()}`}>
                        <td>{result.iboNumber}</td>
                        <td>{result.contentTitle}</td>
                        <td><span className={`picker-item-type type-${result.contentType.toLowerCase()}`}>{result.contentType}</span></td>
                        <td><span className={`status-badge status-${result.status.toLowerCase()}`}>{result.status}</span></td>
                        <td>{result.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== Bulk Assign Tab ===== */}
        {activeTab === 'bulk' && (
          <div className="form-card">
            <form className="assign-form" onSubmit={handleBulkSubmit}>
              <div className="form-group">
                <label>Content & Bundles</label>
                <button type="button" className="open-picker-btn" onClick={openPickerForBulk}>
                  <FontAwesomeIcon icon={faSearch} /> Select Content & Bundles
                </button>
                {bulkSelectedContent.length > 0 ? (
                  <ContentChips items={bulkSelectedContent} onRemove={removeBulkContent} />
                ) : (
                  <p className="no-content-msg">No content selected yet. Click the button above to browse and select.</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="ca-file-upload">CSV File (IBO Numbers)</label>
                <div className="template-download-row">
                  <input type="file" id="ca-file-upload" accept=".csv" onChange={handleFileChange} style={{ flex: 1 }} />
                  <button type="button" className="template-download-btn" onClick={handleDownloadTemplate}>
                    <FontAwesomeIcon icon={faFileExport} /> Download Template
                  </button>
                </div>
                <p className="input-helper">Upload a CSV with IBO numbers (one per row, header: iboNumber)</p>
              </div>

              <div className="form-group date-picker-group">
                <label>Expiry Date <span className="optional-label">(optional)</span></label>
                <DatePicker
                  selected={bulkExpiryDate}
                  onChange={(date) => setBulkExpiryDate(date)}
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  placeholderText="No expiry (permanent access)"
                  isClearable
                  minDate={new Date()}
                />
                <p className="input-helper">Leave empty for permanent access, or set an expiration date</p>
              </div>

              <button type="submit" className="assign-button" disabled={loading || bulkSelectedContent.length === 0 || !file}>
                <FontAwesomeIcon icon={faLink} /> Assign Content
              </button>
            </form>
          </div>
        )}

        {/* ===== Manual Assign Tab ===== */}
        {activeTab === 'manual' && (
          <div className="form-card">
            <form className="assign-form" onSubmit={handleManualSubmit}>
              {entries.map((entry, index) => (
                <div key={entry.id} className="manual-entry-block">
                  <div className="entry-row-header">
                    <span className="entry-number">Entry {index + 1}</span>
                    {entries.length > 1 && (
                      <button type="button" className="remove-entry-button" onClick={() => removeEntry(entry.id)} title="Remove entry">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>

                  {/* IBO Search with button */}
                  <div className="entry-section">
                    <label>IBO Number / Name</label>
                    <div className="ibo-search-row">
                      <div className="autocomplete-container">
                        <input
                          type="text"
                          placeholder="Enter IBO# or name..."
                          value={entry.searchQuery}
                          onChange={(e) => handleIboSearchInput(entry.id, e.target.value)}
                          onBlur={() => handleIboSearchBlur(entry.id, entry)}
                          onKeyDown={(e) => handleIboKeyDown(e, entry.id, entry.searchQuery)}
                          onFocus={() => { if (entry.searchResults.length > 0) updateEntry(entry.id, { showDropdown: true }); }}
                          className={entry.validationError ? 'input-error' : entry.validated ? 'input-valid' : ''}
                        />
                        {entry.searchLoading && <span className="search-spinner"></span>}
                        {entry.validated && !entry.searchLoading && (
                          <FontAwesomeIcon icon={faCheckCircle} className="validation-icon valid" />
                        )}
                        {entry.validationError && !entry.searchLoading && (
                          <FontAwesomeIcon icon={faExclamationTriangle} className="validation-icon invalid" />
                        )}
                        {entry.showDropdown && entry.searchResults.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {entry.searchResults.map((user, idx) => (
                              <div key={idx} className="autocomplete-item" onMouseDown={() => selectUser(entry.id, user)}>
                                <span className="autocomplete-ibo">{user.iboNumber}</span>
                                <span className="autocomplete-name">{user.iboName || 'N/A'}</span>
                                {user.teamName && <span className="autocomplete-team">{user.teamName}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="ibo-search-btn"
                        onClick={() => handleIboSearchClick(entry.id, entry.searchQuery)}
                        disabled={!entry.searchQuery || entry.searchQuery.length < 2}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                    {entry.validationError && <p className="field-error">{entry.validationError}</p>}
                    {entry.validatedViaAmway && <p className="amway-badge">Validated via Amway</p>}
                  </div>

                  {/* User Detail Card */}
                  {entry.userData && (
                    <div className="user-detail-card">
                      <div className="user-detail-row">
                        <span className="user-detail-label">Name</span>
                        <span className="user-detail-value">{entry.userData.iboName || entry.userData.name || 'N/A'}</span>
                      </div>
                      <div className="user-detail-row">
                        <span className="user-detail-label">IBO Number</span>
                        <span className="user-detail-value">{entry.userData.iboNumber}</span>
                      </div>
                      <div className="user-detail-row">
                        <span className="user-detail-label">Team</span>
                        <span className="user-detail-value">{entry.userData.teamName || 'N/A'}</span>
                      </div>
                      <div className="user-detail-row">
                        <span className="user-detail-label">Upline Platinum IBO</span>
                        <span className="user-detail-value">{entry.userData.uplinePlatinumIbo || 'N/A'}</span>
                      </div>
                    </div>
                  )}

                  {/* Content Picker */}
                  <div className="entry-section" style={{ marginTop: 12 }}>
                    <label>Content & Bundles</label>
                    <button type="button" className="open-picker-btn open-picker-btn-sm" onClick={() => openPickerForEntry(entry.id)}>
                      <FontAwesomeIcon icon={faSearch} /> Select Content & Bundles
                    </button>
                    {entry.selectedContent.length > 0 && (
                      <ContentChips items={entry.selectedContent} onRemove={(item) => removeEntryContent(entry.id, item)} />
                    )}
                  </div>

                  {/* Expiry Date */}
                  <div className="entry-section date-picker-group" style={{ marginTop: 12 }}>
                    <label>Expiry Date <span className="optional-label">(optional)</span></label>
                    <DatePicker
                      selected={entry.expiryDate}
                      onChange={(date) => updateEntry(entry.id, { expiryDate: date })}
                      showTimeSelect
                      dateFormat="MMMM d, yyyy h:mm aa"
                      placeholderText="No expiry (permanent access)"
                      isClearable
                      minDate={new Date()}
                    />
                  </div>
                </div>
              ))}

              <button type="button" className="add-entry-button" onClick={addEntry}>
                <FontAwesomeIcon icon={faPlus} /> Add Another Entry
              </button>

              <button
                type="submit"
                className="assign-button"
                disabled={loading || !entries.some(e => e.iboNumber && e.validated && e.selectedContent.length > 0)}
              >
                <FontAwesomeIcon icon={faLink} /> Assign Content
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentAssignPage;
